"use client"

import { apiFetch } from "./client"
import type { ApiGender, UserEnvelope, UserResource } from "./types"
import type { Locale } from "@/lib/i18n"
import type { Gender } from "@/lib/types"

/**
 * Gender enum bridge. The app's third option is "other"; the API calls it
 * "prefer_not_to_say". Mapped at the boundary so the UI copy stays unchanged.
 * (Alignment is also noted in implementation_plan/backend-gaps.md.)
 */
export function toApiGender(g: Gender): ApiGender {
  return g === "other" ? "prefer_not_to_say" : g
}

/**
 * Returns null when the backend has no gender on file (or an unrecognised
 * value) rather than defaulting to "male" — the profile forms require an
 * explicit choice, so a silent default would let an unset gender masquerade as
 * a real selection.
 */
export function fromApiGender(g: string | null | undefined): Gender | null {
  if (g === "prefer_not_to_say") return "other"
  if (g === "female") return "female"
  if (g === "male") return "male"
  return null
}

export interface ProfileInput {
  name?: string
  /** Public handle. Lowercased before send; the backend normalises again. */
  username?: string
  dateOfBirth?: string // ISO date, e.g. "1989-04-17"
  gender?: Gender
  locale?: Locale // "EN" | "ID" — persisted so server-sent notifications match
  photo?: File | null // new profile picture, if the user picked one
}

/**
 * Update the customer profile.
 *
 * The endpoint is multipart `PUT`; PHP only parses multipart bodies on POST, so
 * we use Laravel's `_method=PUT` spoof. A picked photo is sent as the
 * `profile_picture` file part; the response's `profile_picture_url` is a
 * short-lived presigned S3 read URL the backend mints per request.
 */
export async function updateProfile(
  input: ProfileInput
): Promise<UserResource> {
  const fd = new FormData()
  fd.append("_method", "PUT")
  if (input.name !== undefined) fd.append("name", input.name)
  if (input.username !== undefined)
    fd.append("username", input.username.trim().toLowerCase())
  if (input.dateOfBirth)
    fd.append("date_of_birth", new Date(input.dateOfBirth).toISOString())
  if (input.gender) fd.append("gender", toApiGender(input.gender))
  // Backend stores lowercase ISO codes (en/id) to match its lang/ directories.
  if (input.locale) fd.append("locale", input.locale.toLowerCase())
  if (input.photo) fd.append("profile_picture", input.photo)

  const res = await apiFetch<UserEnvelope>("/customer/profile", {
    method: "POST",
    formData: fd,
  })
  return res.data
}

export interface UsernameAvailability {
  available: boolean
  username: string
}

/**
 * Live handle-availability probe (debounced by the caller). Passes an
 * AbortSignal so a superseded in-flight check is cancelled — apiFetch re-throws
 * AbortError untouched. A 422 (bad format) surfaces as a thrown ApiError the
 * caller treats as "invalid", distinct from a 200 { available: false } ("taken").
 */
export async function checkUsernameAvailable(
  username: string,
  signal?: AbortSignal
): Promise<UsernameAvailability> {
  const qs = new URLSearchParams({ username: username.trim().toLowerCase() })
  return apiFetch<UsernameAvailability>(
    `/customer/profile/username-available?${qs.toString()}`,
    { method: "GET", auth: "access", signal }
  )
}
